import 'react-notifications/lib/notifications.css'
import React, { Component } from 'react'
import { withTranslation } from 'react-i18next'
import { NotificationContainer, NotificationManager } from 'react-notifications'
import { FaCog, FaImage, FaUpload, FaCheck, FaTimes } from "react-icons/fa"
import AdminLayout from "../../../components/AdminLayout"

//Components
import AdminService from "../../../services/admin_services"

class AdminSettings extends Component {
    constructor(props) {
        super(props)
        this.state = {
            loading: true,
            updating: false,
            startImages: [],
            currentImage: null,
            selectedImage: null,
            uploadingNew: false,
            newImageData: null,
            newImageSize: 0,
            newImageExtension: null,
            previewUrl: null,
            defaultImageName: `${crypto.randomUUID()}.jpg`,
            imageTimestamp: Date.now(),
        }
    }

    componentDidMount = async () => {
        await this.loadSettings()
    }

    loadSettings = async () => {
        try {
            this.setState({ loading: true })
            const data = await AdminService.getAppSettings()
            this.setState({ 
                startImages: data.start_image.all,
                currentImage: data.start_image.current,
                loading: false 
            })
        } catch (e) {
            NotificationManager.error(e.response && e.response.data ? e.response.data.message : e.message)
            this.setState({ loading: false })
        }
    }

    onImageUpload = async (event) => {
        if (!event.target.files || event.target.files.length === 0) return
        
        const file = event.target.files[0]
        const extension = file.name.split('.').pop().toLowerCase()
        
        if (!['jpg', 'jpeg', 'png'].includes(extension)) {
            NotificationManager.error('Invalid image format. Please use JPG or PNG')
            return
        }
        
        if (file.size > 5 * 1024 * 1024) {
            NotificationManager.error('Image size exceeds 5MB limit')
            return
        }

        const reader = new FileReader()
        reader.onloadend = () => this.processImage(reader, file.size, extension)
        reader.readAsDataURL(file)
    }

    processImage = async (reader, size, extension) => {
        try {
            const base64data = reader.result.split(',')[1]
            this.setState({ 
                newImageData: `data:image/${extension};base64,${base64data}`,
                newImageSize: size,
                newImageExtension: extension,
                previewUrl: reader.result,
                uploadingNew: true,
                selectedImage: this.state.defaultImageName
            })
        } catch (e) {
            NotificationManager.error('Failed to process image')
        }
    }

    selectExistingImage = (imageUrl) => {
        const filename = imageUrl.split('/').pop()
        this.setState({ 
            selectedImage: filename || this.state.defaultImageName,
            uploadingNew: false,
            newImageData: null,
            previewUrl: null
        })
    }

    updateSettings = async () => {
        if (!this.state.selectedImage && !this.state.newImageData) {
            NotificationManager.error('Please select an existing image or upload a new one')
            return
        }

        try {
            this.setState({ updating: true })
            const response = await AdminService.updateAppSettings(
                this.state.selectedImage,
                this.state.newImageData
            )
            NotificationManager.success(response.message || 'Settings updated successfully')
            await this.loadSettings()
            this.setState({ 
                updating: false,
                uploadingNew: false,
                newImageData: null,
                selectedImage: null,
                previewUrl: null,
                defaultImageName: `${crypto.randomUUID()}.jpg`,
                imageTimestamp: Date.now()
            })
        } catch (e) {
            NotificationManager.error(e.response && e.response.data ? e.response.data.message : e.message)
            this.setState({ updating: false })
        }
    }

    cancelSelection = () => {
        this.setState({
            selectedImage: null,
            uploadingNew: false,
            newImageData: null,
            previewUrl: null
        })
    }

    render() {
        return (
            <>
                <AdminLayout 
                    title="App Settings"
                    breadcrumbs={[{ label: 'Settings' }]}
                >
                    {this.state.loading ? (
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
                            <p className="text-gray-600">Loading settings...</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Current Start Image */}
                            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4">
                                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                        <FaImage />
                                        Current Start Screen Image
                                    </h3>
                                    <p className="text-sm text-white/90 mt-1">
                                        This image is displayed when users launch the app
                                    </p>
                                </div>

                                <div className="p-6">
                                    <div className="relative w-full max-w-md mx-auto rounded-xl overflow-hidden shadow-lg border border-gray-200">
                                        <img 
                                            src={`${this.state.currentImage}?t=${this.state.imageTimestamp}`} 
                                            alt="Current start screen"
                                            className="w-full h-auto"
                                        />
                                        <div className="absolute top-3 right-3">
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg shadow-lg">
                                                <FaCheck />
                                                Active
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Upload New Image */}
                            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                                <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-6 py-4">
                                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                        <FaUpload />
                                        Upload New Image
                                    </h3>
                                    <p className="text-sm text-white/90 mt-1">
                                        Upload a new start screen image (JPG/PNG, max 5MB)
                                    </p>
                                </div>

                                <div className="p-6">
                                    <div className="flex flex-col items-center justify-center">
                                        <label className="w-full max-w-md cursor-pointer">
                                            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-purple-500 hover:bg-purple-50 transition-all text-center">
                                                <FaUpload className="mx-auto text-4xl text-gray-400 mb-3" />
                                                <p className="text-gray-700 font-medium mb-1">Click to upload image</p>
                                                <p className="text-sm text-gray-500">JPG or PNG • Max 5MB</p>
                                            </div>
                                            <input 
                                                type="file" 
                                                accept="image/jpeg,image/jpg,image/png"
                                                onChange={this.onImageUpload}
                                                className="hidden"
                                            />
                                        </label>

                                        {this.state.previewUrl && (
                                            <div className="mt-6 w-full max-w-md">
                                                <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                                                <div className="relative rounded-xl overflow-hidden shadow-lg border-2 border-purple-500">
                                                    <img 
                                                        src={this.state.previewUrl} 
                                                        alt="Preview"
                                                        className="w-full h-auto"
                                                    />
                                                    <div className="absolute top-3 right-3">
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500 text-white text-xs font-medium rounded-lg shadow-lg">
                                                            New Upload
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Select from Existing Images */}
                            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                                <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
                                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                        <FaImage />
                                        Select from Existing Images
                                    </h3>
                                    <p className="text-sm text-white/90 mt-1">
                                        Choose from previously uploaded start screen images
                                    </p>
                                </div>

                                <div className="p-6">
                                    {this.state.startImages.length > 0 ? (
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                            {this.state.startImages.map((imageUrl, index) => {
                                                const filename = imageUrl.split('/').pop()
                                                const isSelected = this.state.selectedImage === filename
                                                const isCurrent = imageUrl === this.state.currentImage
                                                
                                                return (
                                                    <div
                                                        key={index}
                                                        onClick={() => this.selectExistingImage(imageUrl)}
                                                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                                            isSelected
                                                                ? 'border-green-500 shadow-lg scale-105'
                                                                : 'border-gray-200 hover:border-green-300 hover:shadow-md'
                                                        }`}
                                                    >
                                                        <div className="aspect-[3/4] bg-gray-100">
                                                            <img 
                                                                src={`${imageUrl}?t=${this.state.imageTimestamp}`} 
                                                                alt={`Start image ${index + 1}`}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        </div>
                                                        {isCurrent && (
                                                            <div className="absolute top-2 left-2">
                                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded shadow-lg">
                                                                    <FaCheck className="text-xs" />
                                                                    Current
                                                                </span>
                                                            </div>
                                                        )}
                                                        {isSelected && (
                                                            <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
                                                                <div className="bg-green-500 rounded-full p-2">
                                                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <FaImage className="text-5xl mx-auto mb-3 text-gray-300" />
                                            <p>No existing images available</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            {(this.state.selectedImage || this.state.uploadingNew) && (
                                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                                    <div className="flex justify-end gap-3">
                                        <button
                                            onClick={this.cancelSelection}
                                            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium flex items-center gap-2"
                                        >
                                            <FaTimes />
                                            Cancel
                                        </button>
                                        {this.state.updating ? (
                                            <div className="px-6 py-2.5 bg-gray-100 text-gray-500 rounded-lg flex items-center gap-2">
                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Updating Settings...
                                            </div>
                                        ) : (
                                            <button 
                                                onClick={this.updateSettings}
                                                className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all font-medium shadow-md hover:shadow-lg flex items-center gap-2"
                                            >
                                                <FaCog />
                                                Update Settings
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </AdminLayout>
                <NotificationContainer />
            </>
        )
    }
}

export default withTranslation()(AdminSettings)
